<script setup>
// SiteUp (modo de horário da IA): define quando o Captain responde sozinho.
// Desligado = responde 24h (padrão). Ligado = só responde nos dias/horários da
// grade abaixo, no fuso escolhido; fora disso a IA fica em silêncio e a conversa
// segue pendente pro atendente humano. Persiste em config.response_* (ver
// Captain::ResponseScheduleService no backend).
import { reactive, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistant: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['submit']);

// Ordem de exibição seg→dom; `day` segue Time#wday (0=domingo).
const DAY_DEFS = [
  { day: 1, label: 'Segunda' },
  { day: 2, label: 'Terça' },
  { day: 3, label: 'Quarta' },
  { day: 4, label: 'Quinta' },
  { day: 5, label: 'Sexta' },
  { day: 6, label: 'Sábado' },
  { day: 0, label: 'Domingo' },
];

const TIMEZONES = [
  'America/Sao_Paulo',
  'America/Bahia',
  'America/Fortaleza',
  'America/Recife',
  'America/Belem',
  'America/Cuiaba',
  'America/Campo_Grande',
  'America/Manaus',
  'America/Rio_Branco',
  'America/Noronha',
];

const state = reactive({
  scheduleEnabled: false,
  timezone: 'America/Sao_Paulo',
  days: [],
});

const buildDays = saved =>
  DAY_DEFS.map(def => {
    const found = Array.isArray(saved)
      ? saved.find(h => h && Number(h.day) === def.day)
      : null;
    return {
      day: def.day,
      label: def.label,
      // Padrão sensato quando não há config salva: seg–sex ligado, fim de semana off.
      enabled: found ? found.enabled === true : def.day >= 1 && def.day <= 5,
      start: (found && found.start) || '08:00',
      end: (found && found.end) || '18:00',
    };
  });

const updateStateFromAssistant = assistant => {
  const config = assistant?.config || {};
  state.scheduleEnabled = config.response_schedule_enabled === true;
  state.timezone = config.response_timezone || 'America/Sao_Paulo';
  state.days = buildDays(config.response_business_hours);
};

const handleSubmit = () => {
  const payload = {
    config: {
      ...props.assistant.config,
      response_schedule_enabled: state.scheduleEnabled,
      response_timezone: state.timezone,
      response_business_hours: state.days.map(d => ({
        day: d.day,
        enabled: d.enabled,
        start: d.start,
        end: d.end,
      })),
    },
  };
  emit('submit', payload);
};

watch(() => props.assistant, updateStateFromAssistant, { immediate: true });
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-2">
      <label class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
        <input v-model="state.scheduleEnabled" type="checkbox" />
        Responder apenas em horário definido
      </label>
      <p class="text-sm text-n-slate-11 italic">
        Desligado (padrão), a IA responde 24h. Ligado, ela só responde sozinha
        nos dias e horários abaixo — fora disso fica em silêncio e a conversa
        segue pendente para um atendente humano assumir.
      </p>
    </div>

    <template v-if="state.scheduleEnabled">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">Fuso horário</label>
        <select
          v-model="state.timezone"
          class="w-64 px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
        >
          <option v-for="tz in TIMEZONES" :key="tz" :value="tz">{{ tz }}</option>
        </select>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          Dias e horários
        </span>
        <div class="flex flex-col gap-2">
          <div
            v-for="day in state.days"
            :key="day.day"
            class="flex items-center gap-3 flex-wrap"
          >
            <label
              class="flex items-center gap-2 text-sm text-n-slate-12 w-32 shrink-0"
            >
              <input v-model="day.enabled" type="checkbox" />
              {{ day.label }}
            </label>
            <template v-if="day.enabled">
              <input
                v-model="day.start"
                type="time"
                class="px-2 py-1 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
              />
              <span class="text-xs text-n-slate-11">até</span>
              <input
                v-model="day.end"
                type="time"
                class="px-2 py-1 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
              />
            </template>
            <span v-else class="text-xs text-n-slate-11">IA não responde</span>
          </div>
        </div>
        <p class="text-sm text-n-slate-11 italic">
          Deixe o início e fim iguais para liberar o dia inteiro. Para faixas que
          viram a noite (ex.: 22:00 até 06:00), basta colocar o fim menor que o
          início.
        </p>
      </div>
    </template>

    <div>
      <Button label="Salvar" @click="handleSubmit" />
    </div>
  </div>
</template>
